import { getSelectedPledgeSurchargeTotal, syncOptionalCoverSurchargeForCreditCover, syncGatewaySurchargePanels } from './pledge-pay-surcharge';
import { sumPledgeBalanceDueOriginal, sumPledgeAmountPaidInputs, sumPledgeAmountPaidForGateway, syncPledgePayGatewayTotals } from './pledge-pay-amount';
import {
    mgrPledgeModalSyncOpenPaymentApplyControls,
    mgrPledgeModalSyncOpenPaymentEmptyPanelLayout,
    syncOpenPaymentStatesSingle,
    recalcOpenApplyCapSingle,
    computeBalanceAppliedSingle,
} from './pledge-pay-open-payment-single';
import {
    mgrPledgeBillGatewayCreditApplyInputFromUseCheckbox,
    mgrPledgeModalCollectGatewayCreditApply,
    mgrPledgeModalHasAnyGatewayCreditApplied,
    mgrPledgeModalSyncGatewayCreditPanelDisplay,
    syncOpenPaymentStatesGateway,
    recalcOpenApplyCapGateway,
    computeBalanceAppliedGateway,
    syncGatewayCreditApplication,
} from './pledge-pay-open-payment-gateway';
import { usesGatewayStackLayout } from './pledge-pay-open-payment-layout';
import { getPledgePayMemberContextUrl } from './pledge-pay-config';
import { getPledgePayMemberContext } from './pledge-pay-member-context';
import {
    clearPledgePayOpenCreditByGateway,
    getPledgePayOpenBalanceAvailable,
    getPledgePayOpenCreditPoolForGateway,
    setPledgePayOpenCreditByGateway,
    setPledgePayOpenCreditTotalAvailable,
    sumPledgePayOpenCreditPools,
} from './pledge-pay-open-credit-context';
import {
    applyPledgePaySavedCardsToUi,
    mgrPledgeModalSyncPaymentMethodPanels,
    mgrPledgeModalSyncPaymentMethodPills,
    MGR_PLEDGE_BILL_SAVED_CARDS,
} from './pledge-pay-method';
import { syncPendingRecurringCashMethod } from './pledge-pay-pending-recurring';
import { isPledgePayCreditCovered, pledgePayBodyFromShell, setPledgePayCreditCovered } from './pledge-pay-utils';
import { renderSavedCards } from '../shared/saved-card-picker.js';
import { autofillMaxIfEmpty } from './open-payment-balance-apply';
import { updatePledgePayNowButtonEnabledState } from './pledge-pay-amount';
import { calculateRecurringPaymentAmount } from './pledge-pay-recurring';
import { mgrPledgeBillMemberAjaxUrl, pledgePayUiRoot, formatCurrency } from './pledge-pay-utils';

function pledgePaySurfaceFromRoot($root) {
    if ($root.is('[data-pledge-pay-surface]')) {
        return String($root.attr('data-pledge-pay-surface') || '');
    }
    const $inside = $root.find('[data-pledge-pay-surface]').first();
    if ($inside.length) {
        return String($inside.attr('data-pledge-pay-surface') || '');
    }
    const $up = $root.closest('[data-pledge-pay-surface]');
    if ($up.length) {
        return String($up.attr('data-pledge-pay-surface') || '');
    }
    return '';
}

function fullyCoveredCopy($root) {
    const surface = pledgePaySurfaceFromRoot($root);
    if (surface !== 'manager_modal' && surface !== 'manager_page') {
        return 'Your pledges are fully covered by your open credit balance.';
    }
    const name = String(
        $root.find('#payment_user_name').val() || $root.find('#payment_member_name').text() || ''
    ).trim();
    if (name === '') {
        throw new Error('fullyCoveredCopy: member name is required');
    }
    return name + "'s pledges are fully covered by their open credit balance.";
}

function setFullyCoveredRowVisible($root, visible) {
    if (visible) {
        $root.find('[data-fully-covered-copy]').text(fullyCoveredCopy($root));
    }
    $root.find('#mgr_pledge_fully_covered_row').toggle(visible);
}

function getOpenBalanceAvailable() {
    return getPledgePayOpenBalanceAvailable();
}

function setOpenBalanceAvailable(amount, $pledgePayBody) {
    const n = setPledgePayOpenCreditTotalAvailable(amount);
    if ($pledgePayBody && $pledgePayBody.length) {
        const $hid = $pledgePayBody.find('#member_open_payment_balance_total');
        if ($hid.length) {
            $hid.val(n.toFixed(2));
        }
        const $amt = $pledgePayBody.find('#mgr_open_payment_available_amt');
        if ($amt.length) {
            $amt.text(formatCurrency(n));
        }
    }
}

function pledgePayBootstrapOpenBalanceAvailable($pledgePayBody) {
    const $hidden = $pledgePayBody.find('#member_open_payment_balance_total');
    const raw = $hidden.length ? (parseFloat(String($hidden.val()).replace(/,/g, '')) || 0) : 0;
    setOpenBalanceAvailable(raw, $pledgePayBody);
}

function clearPledgePayOpenCreditForRecurring($body) {
    if (!$body || !$body.length) {
        return;
    }
    if ($body.find('#mgr_use_open_payment_balance').length) {
        $body.find('#mgr_use_open_payment_balance').prop('checked', false);
    }
    if ($body.find('#mgr_open_payment_apply_amount').length) {
        $body.find('#mgr_open_payment_apply_amount').val('').prop('disabled', true);
    }
    $body.find('.mgr-gateway-use-credit').prop('checked', false);
    $body.find('.mgr-gateway-credit-apply').val('').prop('disabled', true);
    $body.find('.mgr-gateway-credit-panel').addClass('mgr-gateway-credit-inactive');
    $body.find('#mgr_pledge_fully_covered_row').hide();
    mgrPledgeModalSyncOpenPaymentApplyControls($body);
}

function restorePledgePayPaymentMethodForRecurring($body) {
    setPledgePayCreditCovered($body, false);
    const $methodRadios = $body.find('.mgr-pledge-payment-method-radio');
    if (!$methodRadios.length) {
        return;
    }
    $methodRadios.prop('disabled', false);
    $body.find('[data-mgr-pledge-method-pill]')
        .removeClass('is-disabled')
        .prop('disabled', false);
    syncPendingRecurringCashMethod($body);
    if (!$body.find('input[name="mgr_pledge_payment_method_pref"]:enabled:checked').length) {
        $body.find('input[name="mgr_pledge_payment_method_pref"][value="online"]').prop('checked', true);
    }
    mgrPledgeModalSyncPaymentMethodPanels($body);
    mgrPledgeModalSyncPaymentMethodPills($body);
}

function enterCreditCoverMode($modal) {
    if (!isPledgePayCreditCovered($modal)) {
        $modal.data(
            'mgrPledgeMethodBeforeCreditCover',
            $modal.find('input[name="mgr_pledge_payment_method_pref"]:checked').val() || ''
        );
        setPledgePayCreditCovered($modal, true);
    }
    $modal.find('.mgr-pledge-payment-method-radio').prop({ disabled: true, checked: false });
    $modal.find('[data-mgr-pledge-method-pill]')
        .addClass('is-disabled')
        .prop('disabled', true);
    mgrPledgeModalSyncPaymentMethodPanels($modal);
}

function restorePreferredMethodAfterCreditCover($modal) {
    const preferredMethod = String($modal.data('mgrPledgeMethodBeforeCreditCover') || '');
    $modal.removeData('mgrPledgeMethodBeforeCreditCover');
    const $preferred = preferredMethod
        ? $modal.find(`input[name="mgr_pledge_payment_method_pref"][value="${preferredMethod}"]`)
        : $();
    if ($preferred.length && !$preferred.prop('disabled')) {
        $preferred.prop('checked', true);
    }
}

function exitCreditCoverMode($modal) {
    if (isPledgePayCreditCovered($modal)) {
        restorePledgePayPaymentMethodForRecurring(pledgePayBodyFromShell($modal));
        restorePreferredMethodAfterCreditCover($modal);
    }
    if (!$modal.find('input[name="mgr_pledge_payment_method_pref"]:enabled:checked').length) {
        $modal.find('input[name="mgr_pledge_payment_method_pref"][value="online"]').prop('checked', true);
    }
    mgrPledgeModalSyncPaymentMethodPanels($modal);
}

function mgrPledgeModalSyncOpenPaymentStates($modal) {
    const payType = $modal.find('#payment_type').val();
    const mode = $modal.find('input[name="payment_mode"]:checked').val();
    if (payType !== 'pay' || mode === 'recurring') {
        mgrPledgeModalSyncOpenPaymentEmptyPanelLayout($modal, false);
        $modal.find('.mgr-gateway-credit-panel, .mgr-gateway-no-credit-note').hide();
        $modal.find('.mgr-gateway-use-credit').prop('checked', false).prop('disabled', true);
        $modal.find('.mgr-gateway-credit-apply').val('').prop('disabled', true);
        return;
    }
    if (usesGatewayStackLayout($modal)) {
        syncOpenPaymentStatesGateway($modal, mgrPledgeModalSyncOpenPaymentEmptyPanelLayout);
        return;
    }
    syncOpenPaymentStatesSingle($modal, getOpenBalanceAvailable);
}

function mgrPledgeModalSyncOpenPaymentVisibility($modal) {
    mgrPledgeModalSyncSummaryVisibility($modal);
    updatePledgePayNowButtonEnabledState($modal);
    mgrPledgeModalSyncOpenPaymentStates($modal);
    const payType = $modal.find('#payment_type').val();
    const mode = $modal.find('input[name="payment_mode"]:checked').val();
    if (payType === 'pay' && mode !== 'recurring') {
        pledgePayBillUpdateSummary($modal);
    }
}

function mgrPledgeModalRecalcOpenApplyCap($modal) {
    if (usesGatewayStackLayout($modal)) {
        recalcOpenApplyCapGateway($modal);
    } else {
        recalcOpenApplyCapSingle($modal, getOpenBalanceAvailable);
    }
    pledgePayBillUpdateSummary($modal);
    updatePledgePayNowButtonEnabledState($modal);
}

function syncOpenBalanceState($pledgeBody) {
    mgrPledgeModalSyncGatewayCreditPanelDisplay($pledgeBody);
    mgrPledgeModalSyncOpenPaymentStates($pledgeBody);
    pledgePayBillUpdateSummary($pledgeBody);
    updatePledgePayNowButtonEnabledState($pledgeBody);
}

function resetOpenPaymentMemberSection($pledgeBody) {
    setOpenBalanceAvailable(0, $pledgeBody);
    applyPledgePaySavedCardsToUi($pledgeBody, []);
    syncOpenBalanceState($pledgeBody);
}

function refreshManagerPledgeModalOpenPaymentSection($root) {
    const $pledgeBody = $root.is('#pledge_pay_body') ? $root : $root.find('#pledge_pay_body').first();
    if (!$pledgeBody.length) {
        return Promise.resolve();
    }
    const uid = getPledgePayMemberContext().userId;
    clearPledgePayOpenCreditByGateway();
    mgrPledgeModalSyncGatewayCreditPanelDisplay($pledgeBody);
    if ($pledgeBody.find('#mgr_use_open_payment_balance').length) {
        $pledgeBody.find('#mgr_use_open_payment_balance').prop('checked', false);
    }
    if ($pledgeBody.find('#mgr_open_payment_apply_amount').length) {
        $pledgeBody.find('#mgr_open_payment_apply_amount').val('').prop('disabled', true);
    }
    if ($pledgeBody.find('#mgr_open_payment_amount_wrap').length) {
        $pledgeBody.find('#mgr_open_payment_amount_wrap').addClass('mgr-open-credit-apply-muted');
    }
    $pledgeBody.find('.mgr-gateway-use-credit').prop('checked', false).removeData('creditMode');
    $pledgeBody.find('.mgr-gateway-credit-apply').val('').prop('disabled', true);
    $pledgeBody.find('.mgr-gateway-credit-panel').addClass('mgr-gateway-credit-inactive');
    $pledgeBody.find('#mgr_pledge_fully_covered_row').hide();

    mgrPledgeModalSyncSummaryVisibility($pledgeBody);

    const $savedCards = $pledgeBody.find(MGR_PLEDGE_BILL_SAVED_CARDS);
    if ($savedCards.length) {
        renderSavedCards($savedCards, []);
        $pledgeBody.find('.mgr-pay-method-saved').hide();
    }

    if (!uid) {
        resetOpenPaymentMemberSection($pledgeBody);
        return Promise.resolve();
    }

    const url = mgrPledgeBillMemberAjaxUrl(getPledgePayMemberContextUrl(), uid);
    if (!url) {
        resetOpenPaymentMemberSection($pledgeBody);
        return Promise.resolve();
    }
    return $.getJSON(url, (res) => {
        if (getPledgePayMemberContext().userId !== String(uid)) {
            return;
        }
        setPledgePayOpenCreditByGateway((res.success && res.by_gateway) ? res.by_gateway : {});
        const sumPools = sumPledgePayOpenCreditPools();
        const t = (res.success && parseFloat(res.total_remaining)) || sumPools || 0;
        setOpenBalanceAvailable(t, $pledgeBody);
        const savedCards = res && res.success && Array.isArray(res.saved_cards) ? res.saved_cards : [];
        applyPledgePaySavedCardsToUi($pledgeBody, savedCards);
        syncOpenBalanceState($pledgeBody);
    }).fail(() => {
        clearPledgePayOpenCreditByGateway();
        resetOpenPaymentMemberSection($pledgeBody);
    });
}

function mgrPledgeModalSyncSummaryVisibility($modal) {
    const payType = $modal.find('#payment_type').val();
    const show = payType === 'pay';
    $modal.find('#mgr_pledge_summary_section').toggle(show);
    if (!show) {
        $modal.find('#mgr_pledge_fully_covered_row').hide();
    }
}

function mgrPledgeModalSyncPaymentTableFooter($modal) {
    const $foot = $modal.find('#set_payment_table tfoot, #set_pledge_payment_table .mgr-pledge-gateway-payment-table tfoot');
    if (!$foot.length) {
        return;
    }
    const fullMode = $modal.find('#full_payment').is(':checked');
    $foot.toggle(!fullMode);
}

function setPledgePayNowButtonLabel($btn, label) {
    if (!$btn.length) {
        return;
    }
    if ($btn.prop('tagName') === 'BUTTON') {
        if (label === 'Save') {
            $btn.html('<i class="fa-regular fa-floppy-disk mr-1" aria-hidden="true"></i> Save');
        } else {
            $btn.text(label);
        }
    } else {
        $btn.val(label);
    }
}

function mgrPledgeModalMatchesSaveInsteadOfPayButton(online, tableTotal) {
    const o = typeof online === 'number' ? online : parseFloat(String(online ?? '').trim());
    const t = typeof tableTotal === 'number' ? tableTotal : parseFloat(String(tableTotal ?? '').trim());
    if (!Number.isFinite(o) || !Number.isFinite(t)) {
        return false;
    }
    return o < 0.005 && t > 0.005;
}

function mgrPledgeModalSyncPrimaryButton($modal, online, tableTotal) {
    const payType = $modal.find('#payment_type').val();
    const mode = $modal.find('input[name="payment_mode"]:checked').val();
    const $btn = $modal.find('#pay_now_button');
    if (payType !== 'pay') {
        return;
    }
    if (mode === 'recurring') {
        setPledgePayNowButtonLabel($btn, 'Setup Recurring Payment');
        return;
    }
    const isButton = $btn.prop('tagName') === 'BUTTON';
    const amt = '$' + formatCurrency(Math.max(0, online));
    if (isButton) {
        if (mgrPledgeModalMatchesSaveInsteadOfPayButton(online, tableTotal)) {
            $btn.html('<span class="mgr-pay-btn-inner"><i class="fa-regular fa-floppy-disk mr-2" aria-hidden="true"></i>Save</span>');
            return;
        }
        $btn.html(
            `<span class="mgr-pay-btn-inner d-inline-flex align-items-center"><i class="fa-regular fa-credit-card mr-2" aria-hidden="true"></i>Confirm and Pay ${amt}</span>`
        );
        return;
    }
    if (mgrPledgeModalMatchesSaveInsteadOfPayButton(online, tableTotal)) {
        setPledgePayNowButtonLabel($btn, 'Save');
    } else {
        setPledgePayNowButtonLabel($btn, 'Confirm and Pay →');
    }
}

function mgrPledgeBillShouldBypassCardValidation($modal) {
    if (!$modal || !$modal.length) {
        return false;
    }
    const payType = $modal.find('#payment_type').val();
    const mode = $modal.find('input[name="payment_mode"]:checked').val();
    if (payType !== 'pay' || mode === 'recurring') {
        return false;
    }
    pledgePayBillUpdateSummary($modal);
    const $summary = $modal.find('#mgr_pledge_summary_section');
    if (!$summary.length) {
        return false;
    }
    return mgrPledgeModalMatchesSaveInsteadOfPayButton(
        $summary.attr('data-online-due'),
        $summary.attr('data-paying-today-total')
    );
}

function pledgePayBillUpdateSummaryUI($modal, tableTotal, balanceApplied) {
    const roundToCents = (amount) => Math.round(amount * 100) / 100;

    const totalPayingToday = roundToCents(tableTotal);
    const totalBalanceApplied = roundToCents(balanceApplied);
    const totalSurcharge = roundToCents(getSelectedPledgeSurchargeTotal($modal, totalBalanceApplied));
    const amountPaidOnline = roundToCents(Math.max(0, totalPayingToday + totalSurcharge - totalBalanceApplied));
    const totalBalanceDue = roundToCents(sumPledgeBalanceDueOriginal($modal));
    const remainingBalanceAfterPayment = roundToCents(Math.max(0, totalBalanceDue - totalPayingToday));

    $modal.find('#mgr_summary_total_balance_due')
        .text('$' + formatCurrency(totalBalanceDue));

    $modal.find('#mgr_summary_paying_today')
        .html(`<i class="fa-regular fa-credit-card mr-2" aria-hidden="true"></i>$${formatCurrency(amountPaidOnline)}`);

    const $openPaymentLine = $modal.find('#mgr_summary_balance_applied');
    if (totalBalanceApplied > 0.005) {
        $openPaymentLine
            .text('- $' + formatCurrency(totalBalanceApplied))
            .addClass('mgr-summary-open-payment-deduction');
    } else {
        $openPaymentLine
            .text('$' + formatCurrency(0))
            .removeClass('mgr-summary-open-payment-deduction');
    }

    $modal.find('#mgr_summary_remaining_balance')
        .text('$' + formatCurrency(remainingBalanceAfterPayment));

    $modal.find('#mgr_pledge_summary_section').attr({
        'data-online-due': String(amountPaidOnline),
        'data-paying-today-total': String(totalPayingToday),
    });

    const creditFullyCoversOnlineDue = totalPayingToday > 0.005 && amountPaidOnline < 0.005;
    syncOptionalCoverSurchargeForCreditCover($modal, creditFullyCoversOnlineDue);

    if (creditFullyCoversOnlineDue) {
        enterCreditCoverMode($modal);
    } else {
        exitCreditCoverMode($modal);
    }

    if (totalPayingToday <= 0.005) {
        $modal.find('#mgr_pledge_fully_covered_row').hide();
        mgrPledgeModalSyncPrimaryButton($modal, amountPaidOnline, totalPayingToday);
        return;
    }

    const isGrouped = usesGatewayStackLayout($modal);
    const isUsingOpenBalance = isGrouped
        ? balanceApplied > 0.005
        : $modal.find('#mgr_use_open_payment_balance').prop('checked');

    const isFullyCoveredByBalance = !($modal.find('input[name="payment_mode"]:checked').val() === 'recurring')
        && (isGrouped
            ? (balanceApplied > 0.005 && amountPaidOnline < 0.005 && totalPayingToday > 0.005)
            : (
                isUsingOpenBalance &&
                getOpenBalanceAvailable() > 0 &&
                amountPaidOnline < 0.005
            ));

    setFullyCoveredRowVisible($modal, isFullyCoveredByBalance);

    mgrPledgeModalSyncPrimaryButton($modal, amountPaidOnline, totalPayingToday);
}

function bindGatewayCreditEvents($root) {
    const $body = pledgePayBodyFromShell($root);
    const events = 'input.pledgePayCredit change.pledgePayCredit';
    $body.off(events, '.mgr-gateway-use-credit').on(events, '.mgr-gateway-use-credit', function () {
        const $root = pledgePayUiRoot($(this));
        const $panel = $(this).closest('.mgr-gateway-credit-panel');
        const $inp = $panel.find('.mgr-gateway-credit-apply');
        const isChecked = $(this).prop('checked');
        $(this).data('creditMode', isChecked ? 'manual' : 'off');
        if (isChecked) {
            $inp.prop('disabled', false);
            $panel.removeClass('mgr-gateway-credit-inactive');
            const gid = String($(this).data('gateway-id') || '');
            const pool = getPledgePayOpenCreditPoolForGateway(gid);
            const groupPay = sumPledgeAmountPaidForGateway($root, gid);
            if ($inp.val() === '' || $inp.val() === null) {
                const def = Math.min(pool, groupPay);
                if (def > 0) {
                    $inp.val(def.toFixed(2));
                }
            }
            mgrPledgeModalRecalcOpenApplyCap($root);
        } else {
            $inp.val('').prop('disabled', true);
            $panel.addClass('mgr-gateway-credit-inactive');
            pledgePayBillUpdateSummary($root);
            updatePledgePayNowButtonEnabledState($root);
        }
    });

    $body.off(events, '.mgr-gateway-credit-apply').on(events, '.mgr-gateway-credit-apply', function () {
        $(this).closest('.mgr-gateway-credit-panel').find('.mgr-gateway-use-credit').data('creditMode', 'manual');
        const $root = pledgePayUiRoot($(this));
        pledgePayBillUpdateSummary($root);
        updatePledgePayNowButtonEnabledState($root);
    });
}

function pledgePayBillUpdateSummary($modal) {
    if (!$modal || !$modal.length) {
        return;
    }
    bindGatewayCreditEvents($modal);
    syncPledgePayGatewayTotals($modal);
    const payType = $modal.find('#payment_type').val();
    const mode = $modal.find('input[name="payment_mode"]:checked').val();
    if (payType !== 'pay' || mode === 'recurring') {
        return;
    }
    if (usesGatewayStackLayout($modal)) {
        syncGatewayCreditApplication($modal);
    }
    if ($modal.find('.mgr-pledge-gateway-stack').length) {
        syncGatewaySurchargePanels($modal);
    }
    const $summary = $modal.find('#mgr_pledge_summary_section');
    if (!$summary.length) {
        return;
    }
    const tableTotal = sumPledgeAmountPaidInputs($modal);

    const balanceApplied = usesGatewayStackLayout($modal)
        ? computeBalanceAppliedGateway($modal)
        : computeBalanceAppliedSingle($modal, getOpenBalanceAvailable);

    pledgePayBillUpdateSummaryUI($modal, tableTotal, balanceApplied);
}

function pledgePayBillSyncSummaryColumnLayout($body, isRecurring) {
    if (!$body || !$body.length) {
        return;
    }
    const $home = $body.find('#mgr_recurring_fields_home');
    if ($home.length) {
        $home.show();
    }

    if (isRecurring) {
        clearPledgePayOpenCreditForRecurring($body);
        restorePledgePayPaymentMethodForRecurring($body);
        mgrPledgeModalSyncOpenPaymentStates($body);
        mgrPledgeModalSyncPaymentMethodPanels($body);
        calculateRecurringPaymentAmount($body);
        return;
    }

    const t = getOpenBalanceAvailable();
    if (t <= 0) {
        if ($body.find('#mgr_use_open_payment_balance').length) {
            $body.find('#mgr_use_open_payment_balance').prop('checked', false);
        }
        if ($body.find('#mgr_open_payment_apply_amount').length) {
            $body.find('#mgr_open_payment_apply_amount').val('');
        }
        mgrPledgeModalSyncOpenPaymentApplyControls($body);
    }

    mgrPledgeModalSyncGatewayCreditPanelDisplay($body);
    mgrPledgeModalSyncOpenPaymentStates($body);
    mgrPledgeModalRecalcOpenApplyCap($body);
}

function pledgePaymentBodySyncOpenPaymentSummary($body, isRecurring) {
    if (!$body || !$body.length || !$body.is('#pledge_pay_body')) {
        return;
    }
    if ($body.find('#member_open_payment_balance_total').length) {
        pledgePayBootstrapOpenBalanceAvailable($body);
    }
    if (!$body.find('#mgr_pledge_summary_section').length) {
        pledgePayBillSyncSummaryColumnLayout($body, !!isRecurring);
        return;
    }
    mgrPledgeModalSyncSummaryVisibility($body);
    pledgePayBillSyncSummaryColumnLayout($body, !!isRecurring);
    if (!isRecurring) {
        pledgePayBillUpdateSummary($body);
    }
    updatePledgePayNowButtonEnabledState($body);
}

$(document).on('change', '#mgr_use_open_payment_balance', function () {
    const $root = $(this).closest('#pledge_pay_body');
    if (!$root.length) {
        return;
    }
    const $applyInput = $root.find('#mgr_open_payment_apply_amount');
    const isChecked = $(this).prop('checked');

    if (isChecked) {
        mgrPledgeModalSyncOpenPaymentApplyControls($root);
        mgrPledgeModalRecalcOpenApplyCap($root);

        const amtDue = sumPledgeAmountPaidInputs($root);
        const openAvail = getOpenBalanceAvailable();
        autofillMaxIfEmpty($applyInput, openAvail, amtDue);
        mgrPledgeModalRecalcOpenApplyCap($root);
    } else {
        $applyInput.val('');
        mgrPledgeModalSyncOpenPaymentApplyControls($root);
        pledgePayBillUpdateSummary($root);
        updatePledgePayNowButtonEnabledState($root);
    }
});

$(document).on('input change', '#mgr_open_payment_apply_amount', function () {
    const $root = $(this).closest('#pledge_pay_body');
    if ($root.length) {
        pledgePayBillUpdateSummary($root);
        updatePledgePayNowButtonEnabledState($root);
    }
});

$(document).on('change', '#setPledgePaymentModal #cover_surcharge, #pledge_pay_body #cover_surcharge, .mgr-gateway-cover-surcharge', function () {
    const $root = pledgePayUiRoot($(this));
    pledgePayBillUpdateSummary($root);
    updatePledgePayNowButtonEnabledState($root);
});

$(document).on('pledge-pay:summary-refresh', '#setPledgePaymentModal, #pledge_pay_body', function () {
    const $root = pledgePayUiRoot($(this));
    pledgePayBillUpdateSummary($root);
    updatePledgePayNowButtonEnabledState($root);
});

export {
    getOpenBalanceAvailable,
    mgrPledgeBillShouldBypassCardValidation,
    mgrPledgeModalRecalcOpenApplyCap,
    mgrPledgeModalSyncOpenPaymentStates,
    mgrPledgeModalSyncOpenPaymentVisibility,
    mgrPledgeModalSyncPaymentTableFooter,
    mgrPledgeModalSyncPrimaryButton,
    mgrPledgeModalSyncSummaryVisibility,
    mgrPledgeModalMatchesSaveInsteadOfPayButton,
    pledgePayBillSyncSummaryColumnLayout,
    pledgePayBillUpdateSummary,
    pledgePayBillUpdateSummaryUI,
    pledgePayBootstrapOpenBalanceAvailable,
    pledgePaymentBodySyncOpenPaymentSummary,
    refreshManagerPledgeModalOpenPaymentSection,
    restorePledgePayPaymentMethodForRecurring,
    setOpenBalanceAvailable,
    setPledgePayNowButtonLabel,
};
